<template lang="pug">
  .b-crm-lead-detail-history-ui
    //- Лента не скроллится внутри себя: она растёт во всю свою высоту, а
    //- прокручивается боковая шторка целиком
    ._events
      ._created-row
        span._created-date {{ createdAt }}
        span._created-label Заявка создана
      ._divider
      template(v-for="event in events")
        //- Каждая из сторон — либо этап, либо статус закрытия (true/false),
        //- см. sideItems. Причина отказа приходит вместе с записью
        ._stage-change(v-if="event.type === 'stage'" :key="event.id")
          span._event-date {{ eventDate(event) }}
          span._event-author(v-if="authorName(event)") {{ authorName(event) }}
          //- пара «было → стало» в одном блоке: на узкой шторке она переносится
          //- на следующую строку целиком, а не разрывается между строками
          ._stage-transition
            BaseStatus._stage-badge.-from(:items="sideItems(event.info.old)" :value="sideValue(event.info.old)")
            BaseStatus._stage-badge(:items="sideItems(event.info.new)" :value="sideValue(event.info.new)")
          span._note-text(v-if="reasonName(event)") {{ reasonName(event) }}
        ._note(v-else-if="event.type === 'manager'" :key="event.id")
          span._event-date {{ eventDate(event) }}
          span._event-author(v-if="authorName(event)") {{ authorName(event) }}
          span._note-text Ответственный: {{ userName(event, event.info.old) }} → {{ userName(event, event.info.new) }}
        ._note(v-else-if="event.type === 'comment-delete'" :key="event.id")
          span._event-date {{ eventDate(event) }}
          span._event-author(v-if="authorName(event)") {{ authorName(event) }}
          span._note-text {{ deletedText(event) }}
        CrmLeadDetailComment(
          v-else
          :key="event.id"
          :comment="event"
          :leadId="leadId"
          :editable="canEdit(event)"
          :removable="canRemove(event)"
          @edit="$emit('editComment', $event)"
          @remove="$emit('removeComment', $event)"
          )
    ._composer
      CrmLeadDetailCommentForm(ref="form" :sending="sending" @submit="$emit('submitComment', $event)")
</template>

<script>
import utils from '@/utils/common';
import { CLOSING_COLUMNS } from '@/model/crm';

import BaseStatus from '@/components/BaseStatus.vue';
import CrmLeadDetailComment from '@/components/crm/CrmLeadDetailComment.vue';
import CrmLeadDetailCommentForm from '@/components/crm/CrmLeadDetailCommentForm.vue';

// Комментарий «протухает» через сутки после публикации: дальше сервер не даёт
// ни изменить его, ни удалить (422 «comment is outdated») — ни автору, ни
// владельцу компании с заместителем. После этого срока прячем пункты меню
const COMMENT_LIFETIME_MS = 24 * 60 * 60 * 1000;

export default {
  name: 'CrmLeadDetailHistoryUi',
  components: { BaseStatus, CrmLeadDetailComment, CrmLeadDetailCommentForm },
  props: {
    // этапы воронки — только чтобы раскрасить плашки смены этапа: в самой
    // записи истории приходят лишь id и название
    stages: {
      type: Array,
      default: () => [],
    },
    // лента событий, старые сверху
    events: {
      type: Array,
      required: true,
    },
    // заявка ленты — тайлам комментариев нужна для поиска своих уведомлений
    leadId: {
      type: Number,
      required: true,
    },
    createdAt: String,
    // id пользователей, чьи комментарии можно править и удалять
    currentUserId: Number,
    canModerate: Boolean,
    sending: Boolean,
  },
  methods: {
    eventDate(event) {
      return utils.convertDateToString(event.createdAt, 'short', true);
    },
    /**
     * Сторона перехода в записи истории: этап `{ id, name }` или статус
     * закрытия — true (успех) либо false (отказ). Закрытие рисуем той же
     * плашкой, что и колонку доски, чтобы лента говорила с ней одними словами
     * и цветами. У этапа в записи только id и название, цвет берём из этапов
     * воронки.
     *
     * Отдаём списком из одного элемента: BaseStatus рисует выбранный элемент,
     * а выбирать здесь не из чего — это уже история.
     */
    sideItems(side) {
      if (typeof side === 'boolean') {
        const column = CLOSING_COLUMNS.find(item => item.isSuccessful === side);
        return [{ id: column.id, name: column.name, color: column.color }];
      }
      if (!side) return [];

      const color = this.stages.find(item => item.id === side.id)?.color;
      return [{ id: side.id, name: side.name, color }];
    },
    sideValue(side) {
      return this.sideItems(side)[0]?.id ?? null;
    },
    // Причина заполнена только у перехода в отказ — снимок на момент записи,
    // так что в справочник не лезем
    reasonName(event) {
      return event.info?.rejectionReason?.name ?? '';
    },
    // у записи о смене ответственного info хранит id пользователей, а сами
    // они лежат в users
    userName(event, userId) {
      const user = (event.users ?? []).find(item => item.id === userId);
      return user ? utils.getUserName(user) : 'не назначен';
    },
    /** Кто внёс изменение; у комментариев автора показывает сам тайл. */
    authorName(event) {
      return event.author ? utils.getUserName(event.author) : '';
    },
    // Имя того, кто удалил, стоит отдельной подписью вместе с остальными
    // записями (см. authorName), поэтому здесь остаётся только действие
    deletedText(event) {
      return this.authorName(event) ? 'удалил комментарий' : 'Комментарий удалён';
    },
    /**
     * Комментарий ещё можно править и удалять: сутки с момента публикации не
     * прошли. Для черновика createdAt — это момент публикации, а не создания,
     * так что отсчёт одинаковый для всех записей.
     */
    isFresh(event) {
      const createdAt = new Date(event.createdAt).getTime();
      if (Number.isNaN(createdAt)) return false;

      return Date.now() - createdAt < COMMENT_LIFETIME_MS;
    },
    canEdit(event) {
      return event.author?.id === this.currentUserId && this.isFresh(event);
    },
    canRemove(event) {
      return (this.canModerate || event.author?.id === this.currentUserId)
        && this.isFresh(event);
    },
    /** Вызывает родитель после успешной отправки. */
    resetForm() {
      this.$refs.form?.reset();
    },
  },
};
</script>

<style scoped lang="scss">
.crm-lead-detail-history-ui {
  background: var(--color-white);
  border-radius: 8px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;

  // своего padding нет: его даёт __events ниже — так первая строка отбивается
  // от края ровно так же, как остальные события
  &__created-row {
    display: flex;
    align-items: center;
    gap: 19px;
    font-size: 11px;
    color: var(--color-dark-gray);
    opacity: 0.5;
  }

  &__divider {
    flex-shrink: 0;
    height: 1px;
    background: var(--color-purple);
  }

  // Скролла внутри нет: лента растёт во всю высоту, прокручивается шторка.
  // Отступы совпадают с собственным padding ._details (CrmLeadDetailUi)
  &__events {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 24px;
  }

  // Строки событий переносятся: в узкой шторке дата, автор, плашки и причина
  // отказа в одну строку не встают, а сжимать плашки нельзя — текст в них
  // обрезался бы
  &__stage-change, &__note {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 12px;
    font-size: 11px;
    color: var(--color-dark-gray);
  }

  &__stage-transition {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 12px;
  }

  &__note-text {
    opacity: 0.7;
    // длинная причина отказа или имена ответственных переносятся текстом
    min-width: 0;
    overflow-wrap: anywhere;
  }

  &__event-date {
    opacity: 0.5;
    flex-shrink: 0;
  }

  // Автор — тот, кто внёс изменение. Заметнее даты и самого действия: в ленте
  // обычно ищут именно «кто это сделал». Не сжимается, но длинное имя
  // обрезаем, чтобы оно не выдавило плашки этапов за край
  &__event-author {
    flex-shrink: 0;
    max-width: 40%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
  }

  // Плашки этапов в ленте мельче обычных: обращаемся к простому
  // классу-зацепке BaseStatus, а не к его БЭМ-имени
  &__stage-change ::v-deep .base-status {
    min-width: 0;
    flex-shrink: 0;
    padding: 3px 8px;
    font-size: 11px;
    line-height: 1.15;
  }

  // предыдущий этап — приглушённый и зачёркнутый
  &__stage-badge--from {
    opacity: 0.5;
    text-decoration: line-through;
  }

  &__composer {
    // по бокам и снизу — те же 24px, что у __events выше и у ._details
    // (CrmLeadDetailUi); сверху меньше: это внутренний отступ от ленты
    padding: 10px 24px 24px;
    border-top: 1px solid var(--color-purple);
  }

  @media (max-width: 576px) {
    &__events, &__composer {
      padding-left: 16px;
      padding-right: 16px;
    }
  }
}
</style>
